import { toHtml } from 'hast-util-to-html';
import { noop } from 'lodash';
import { common, createLowlight } from 'lowlight';
import { Root } from 'lowlight/lib';
import { Grammar } from 'prismjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getPrism } from '../utils/prism';
import { textHtml } from '../utils/text';

const lowlight = createLowlight(common);

let Prism: typeof import('prismjs') | null = null;

/** Подсветка кода с помощью Prism или lowlight */
function highlightPrismOrLowlight(code: string, detectedLanguage: string) {
  let grammar = Prism?.languages[detectedLanguage];
  let lowlightRoot: Root;
  /** lowlight умеет определять язык, пробуем получить grammar */
  if (!grammar) {
    lowlightRoot = lowlight.highlightAuto(code);

    if (lowlightRoot.data?.language) {
      grammar = Prism?.languages[lowlightRoot.data?.language];
    }
  } else {
    return Prism?.highlight(code, grammar, detectedLanguage);
  }

  /** Если не получилось отрисовать через Prism, используем lowlight */
  return toHtml(lowlightRoot);
}

export const useMarkedText = ({ text }: { text: string }) => {
  const currentText = useRef(text);
  const firstRender = useRef(true);

  const highlight = useCallback((code: string, lang: string) => {
    if (!Prism || !Prism.languages[lang]) {
      Promise.allSettled([getPrism(), Promise.resolve<undefined | { default: Grammar }>(undefined)])
        .then(([prismPromise, languagePromise]) => {
          if (prismPromise.status === 'fulfilled') {
            Prism = prismPromise.value;
          }

          if (Prism && languagePromise.status === 'fulfilled' && languagePromise.value) {
            Prism.languages[lang] = languagePromise.value.default;
          }

          //TODO: Надо убрать отсюда setParsedText
          setParsedText(
            textHtml(currentText.current, {
              highlight: highlightPrismOrLowlight,
            }),
          );
        })
        .catch(noop);
    } else {
      return highlightPrismOrLowlight(code, lang);
    }
  }, []);

  const [parsedText, setParsedText] = useState(() => {
    return textHtml(text, {
      highlight,
    });
  });

  useEffect(() => {
    currentText.current = text;
  });

  useEffect(() => {
    if (!firstRender.current) {
      setParsedText(
        textHtml(text, {
          highlight,
        }),
      );
    }
    firstRender.current = false;
  }, [text]);

  return { parsedText };
};
